// Product photos. A phone photo is several megabytes; it is made small in the
// browser before upload, so it fits the Worker's 2 MB limit and loads fast on
// a waiter's phone. Stored privately in R2 (see apps/api: /v1/…/images).

/** Longest side of a stored photo, in pixels. */
export const PHOTO_MAX_SIDE = 1000

/** The size that fits within `max` on its longest side, never enlarging. */
export function fitWithin(
  width: number,
  height: number,
  max = PHOTO_MAX_SIDE,
): { width: number; height: number } {
  const longest = Math.max(width, height)
  if (longest <= max || longest === 0) return { width, height }
  const scale = max / longest
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  }
}

function toBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, type, quality))
}

/**
 * A photo ready to upload: scaled to fit PHOTO_MAX_SIDE and saved as WebP
 * (JPEG where the browser cannot write WebP). Throws when the file is not a
 * picture the browser can read.
 */
export async function preparePhoto(file: File): Promise<File> {
  let bitmap: ImageBitmap
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' })
  } catch {
    throw new Error('That file is not a picture this device can read. Use a JPEG or PNG.')
  }
  const { width, height } = fitWithin(bitmap.width, bitmap.height)
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const context = canvas.getContext('2d')
  if (!context) throw new Error('This browser cannot resize pictures.')
  // A white ground, so a transparent PNG does not turn black as JPEG.
  context.fillStyle = '#fff'
  context.fillRect(0, 0, width, height)
  context.drawImage(bitmap, 0, 0, width, height)
  bitmap.close()
  let blob = await toBlob(canvas, 'image/webp', 0.82)
  // Browsers that cannot write WebP hand back a PNG instead.
  if (!blob || blob.type !== 'image/webp') blob = await toBlob(canvas, 'image/jpeg', 0.85)
  if (!blob) throw new Error('This browser cannot resize pictures.')
  const ext = blob.type === 'image/webp' ? 'webp' : 'jpg'
  return new File([blob], `photo.${ext}`, { type: blob.type })
}
